import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu } from '@headlessui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AssetQuery, Status } from '~/generated/graphql';

type Props = {
  asset?: AssetQuery['asset'];
  onToggleStatus?: () => void;
};

// Hook-free AssetMenu with framer-motion animations and status toggle handler from parent
export const AssetMenu = memo(function AssetMenu({
  asset,
  onToggleStatus,
}: Props) {
  const { t } = useTranslation();
  const { siteId } = useParams();
  const navigate = useNavigate();
  if (!asset) return null;
  // Determine the correct routes based on context
  const editRoute = siteId
    ? `/sites/${siteId}/assets/${asset.id}/edit`
    : `/assets/${asset.id}/edit`;

  const deployRoute = siteId
    ? `/sites/${siteId}/assets/${asset.id}/deploy`
    : `/assets/${asset.id}/deploy`;

  const cloneRoute = siteId ? `/sites/${siteId}/assets/new` : `/assets/new`;

  const handleClone = () => {
    navigate(cloneRoute, {
      state: { cloneFrom: asset },
    });
  };

  return (
    <Menu as='div' className='relative flex text-left'>
      {({ open }) => (
        <>
          <Menu.Button className='duration-250 flex h-10 w-10 items-center justify-center rounded-full text-secondary transition-all hover:bg-gray-100 hover:text-primary active:bg-gray-200'>
            <FontAwesomeIcon
              icon={faEllipsisVertical as IconProp}
              className='h-5 w-5 hover:text-brand'
            />
          </Menu.Button>

          <AnimatePresence>
            {open && (
              <motion.div
                className='absolute right-0 top-8 z-[1000] mt-3'
                initial={{
                  opacity: 0,
                  scale: 0.8,
                  transformOrigin: 'top right',
                }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  transformOrigin: 'top right',
                }}
                exit={{
                  opacity: 0,
                  scale: 0.8,
                  transformOrigin: 'top right',
                }}
                transition={{
                  duration: 0.15,
                  ease: [0.16, 1, 0.3, 1],
                }}
              >
                <Menu.Items className='z-50 w-44 rounded-md border border-gray55 bg-white shadow-lg focus:outline-none'>
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to={editRoute}
                        onClick={() => {
                          // Store current tab in sessionStorage before navigating to edit form
                          const currentTab =
                            new URLSearchParams(window.location.search).get(
                              'tab'
                            ) || '0';
                          sessionStorage.setItem(
                            `assetTab_${asset.id}`,
                            currentTab
                          );
                        }}
                        className={`block w-full cursor-pointer border-b border-outline-variant px-4 py-5 text-left text-sm font-medium last:border-b-0 ${
                          active ? 'text-brand' : 'text-copy-body'
                        }`}
                      >
                        {t('editAssetSummary')}
                      </Link>
                    )}
                  </Menu.Item>

                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to={deployRoute}
                        onClick={() => {
                          // Store deployment tab (tab 2) in sessionStorage before navigating to deploy form
                          sessionStorage.setItem(`assetTab_${asset.id}`, '2');
                        }}
                        className={`block w-full cursor-pointer border-b border-outline-variant px-4 py-5 text-left text-sm font-medium last:border-b-0 ${
                          active ? 'text-brand' : 'text-copy-body'
                        }`}
                      >
                        {asset?.deployment ? t('redeploy') : t('deploy')}
                      </Link>
                    )}
                  </Menu.Item>

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={onToggleStatus}
                        className={`block w-full cursor-pointer border-b border-outline-variant px-4 py-5 text-left text-sm font-medium last:border-b-0 ${
                          active ? 'text-brand' : 'text-copy-body'
                        }`}
                      >
                        {asset.status === Status.Active
                          ? t('makeInactive')
                          : t('makeActive')}
                      </button>
                    )}
                  </Menu.Item>

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleClone}
                        className={`block w-full cursor-pointer border-b border-outline-variant px-4 py-5 text-left text-sm font-medium last:border-b-0 ${
                          active ? 'text-brand' : 'text-copy-body'
                        }`}
                      >
                        {t('cloneAsset')}
                      </button>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </Menu>
  );
});
